import { useRef, useState } from "react";
import BlockTitle from "../_BlockTitle";
import classNames from "classnames";
import styles from "./styles/HTMLBlock.module.scss";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import WatsonApi from "../../../../backends/WatsonApi";
import NotificationManager from "../../../notifications/NotificationManager";
import { EditDropDownItem } from "../EditDropDown";
import FontAwesome from "../../../utilities/FontAwesome";
import { useStores } from "../../../../stores/useStores";
import { ErrorBoundary } from "../../../utilities/rollbar/ErrorBoundary";
import DangerouslySetHtmlContent from "../../../utilities/DangerouslySetHtmlContent";
import { HtmlEditor, serializeSlateToHtml } from "../../../editors/HtmlEditor";
import type { SlateEditor } from "platejs";
import { NodeType } from "../../../editors/utils";
import { PlaceholderPlugin } from "@platejs/media/react";
import { compressImage } from "../../../../utils/DataUtilities";
import { getCdnUrl } from "../../../../utils/SchoolBlocksUtilities";
import { useFeatureFlags } from "../../../../utils/featureFlags/useFeatureFlags";
import { useAdaCheckResults } from "../../../../hooks/useAdaCheckResults";
import { Box, Button } from "@radix-ui/themes";

const htmlBlockContainerClassName = classNames({
  [styles.htmlBlockContainer]: true,
});

const enum SaveButtonText {
  SAVE = "Evaluate ADA and Save Changes",
  ADA_EVALUATE = "Evaluating Accessibility...",
  SAVING_IMAGES = "Saving Images...",
  SAVING_CONTENT = "Saving Content...",
}

const HtmlTerrace = observer(
  (props: { blockObj: IHTmlTerrace; aboveTheFold: boolean }) => {
    const { gridStore } = useStores();
    const { html_terrace_html_editor } = useFeatureFlags();
    const { checkAda, errorContent, adaCheckErrorCount } = useAdaCheckResults();
    const [saveButtonText, setSaveButtonText] = useState(SaveButtonText.SAVE);

    const htmlEditorRef = useRef<{ editor: SlateEditor }>(null);
    const [code, setCode] = useState(
      props.blockObj.json_data?.html?.code || "",
    );
    const [editMode, setEditMode] = useState(false);

    const handleSaveHtmlEditor = async (skipAdaCheck: boolean) => {
      try {
        const editor = htmlEditorRef.current?.editor;
        if (!editor) return;

        if (!skipAdaCheck) {
          const html = await serializeSlateToHtml(editor);
          setSaveButtonText(SaveButtonText.ADA_EVALUATE);
          const { adaCheckErrorCount } = await checkAda(html);
          if (adaCheckErrorCount > 0) return;
        }

        const client = await WatsonApi();
        const mediaPlaceholders = editor.api.nodes({
          match: { type: NodeType.MEDIA_PLACEHOLDER },
          at: [],
        });
        for (let placeholder of mediaPlaceholders) {
          setSaveButtonText(SaveButtonText.SAVING_IMAGES);
          const placeholderApi = editor.getApi(PlaceholderPlugin);
          const placeholderId = placeholder[0].id as string;
          const file =
            placeholderApi.placeholder.getUploadingFile(placeholderId);
          const compressedFile = await compressImage(file, 2);

          const response =
            await client.apis.organizations.organizations_terraces_upload_image(
              {
                organization_pk: props.blockObj.organization_id,
                id: props.blockObj.id,
                file: compressedFile,
              },
            );
          const imageUrl = response.body.url;
          editor.tf.setNodes(
            {
              type: NodeType.IMAGE,
              url: getCdnUrl(imageUrl),
            },
            {
              at: placeholder[1],
            },
          );
        }

        const html = await serializeSlateToHtml(editor);

        setSaveButtonText(SaveButtonText.SAVING_CONTENT);
        const response =
          await client.apis.organizations.organizations_terraces_partial_update(
            {
              organization_pk: props.blockObj.organization_id,
              id: props.blockObj.id,
              data: {
                json_data: {
                  html: {
                    code: html,
                  },
                },
              },
            },
          );
        setCode(html);
        runInAction(() => {
          const updatedTerrace = JSON.parse(response.data);
          gridStore.blocks.set(updatedTerrace.id, updatedTerrace);
        });
        NotificationManager.success("Terrace successfully saved!");
      } catch {
        setCode(props.blockObj.json_data?.html?.code || "");
        NotificationManager.error("Something went wrong! Please try again.");
      } finally {
        setSaveButtonText(SaveButtonText.SAVE);
      }
    };

    const handleSaveRawEditor = async (skipAdaCheck: boolean) => {
      try {
        if (!skipAdaCheck) {
          setSaveButtonText(SaveButtonText.ADA_EVALUATE);
          const { adaCheckErrorCount } = await checkAda(code);
          if (adaCheckErrorCount > 0) return;
        }

        const client = await WatsonApi();
        const response =
          await client.apis.organizations.organizations_terraces_partial_update(
            {
              organization_pk: props.blockObj.organization_id,
              id: props.blockObj.id,
              data: {
                json_data: {
                  html: {
                    code,
                  },
                },
              },
            },
          );
        setEditMode(false);
        runInAction(() => {
          const updatedTerrace = JSON.parse(response.data);
          gridStore.blocks.set(updatedTerrace.id, updatedTerrace);
        });
        NotificationManager.success("Terrace successfully saved!");
      } catch {
        setCode(props.blockObj.json_data?.html?.code || "");
        NotificationManager.error("Something went wrong! Please try again.");
      }
    };

    const handleSave = async (skipAdaCheck: boolean) => {
      if (html_terrace_html_editor) {
        await handleSaveHtmlEditor(skipAdaCheck);
      } else {
        await handleSaveRawEditor(skipAdaCheck);
      }
    };

    const htmlTerraceDropDownItems: EditDropDownItem[] = [
      {
        title: "Edit HTML",
        icon: (
          <FontAwesome prefix={"fas"} fixedWidth className="fa-pencil-alt" />
        ),
        onClick: () => setEditMode((v) => !v),
      },
    ];

    let editor;
    if (html_terrace_html_editor) {
      editor = (
        <HtmlEditor
          editorClassName={styles.editor}
          value={code}
          defaultEditor={"html"}
          ref={htmlEditorRef}
        />
      );
    } else {
      editor = (
        <>
          <div>
            <p>Enter Custom HTML below:</p>
          </div>
          <textarea onChange={(e) => setCode(e.target.value)} value={code} />
        </>
      );
    }

    return (
      <article className={htmlBlockContainerClassName}>
        <BlockTitle
          blockObj={props.blockObj}
          aboveTheFold={props.aboveTheFold}
          dropDownItems={htmlTerraceDropDownItems}
          allowSave={true}
          showViewMore={false}
        />
        {editMode ? (
          <>
            {editor}
            <Box pt={"2"}>
              {errorContent}
              <Box style={{ textAlign: "left" }}>
                {adaCheckErrorCount > 0 && (
                  <Box pr={"2"} display={"inline-block"}>
                    <Button
                      variant={"outline"}
                      onClick={() => handleSave(true)}
                    >
                      Ignore Errors and Save Anyway
                    </Button>
                  </Box>
                )}
                <Box pr={"2"} display={"inline-block"}>
                  <Button
                    onClick={() => handleSave(false)}
                    disabled={saveButtonText !== SaveButtonText.SAVE}
                  >
                    {saveButtonText}
                  </Button>
                </Box>
                <Box display={"inline-block"}>
                  <Button
                    variant={"outline"}
                    onClick={() => {
                      setCode(props.blockObj.json_data?.html?.code || "");
                      setEditMode(false);
                    }}
                  >
                    Exit
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <ErrorBoundary userGenerated={true}>
            <div className={html_terrace_html_editor ? "sb-scoped-html" : ""}>
              <DangerouslySetHtmlContent html={code} />
            </div>
          </ErrorBoundary>
        )}
      </article>
    );
  },
);

export default HtmlTerrace;
